import { Directive, EventEmitter, Output } from '@angular/core';
import { ID } from '@datorama/akita';

import { appearance } from '../mapper.appearance';
import { MapperComponent } from '../mapper.component';

declare let fabric: any;

export interface MappingFieldOptions {
  // prevents scale blurring
  borderScaleFactor?: number;
  counterSign: boolean;
  height: number;
  left: number;
  // If width set to 0, then setting any scale will equal pixels desired
  // Example: this.set({ scale: 215 }) will result in width being 215 pixels
  // Setting to 1 somehow calculates it as 2 so scaling will double
  noScaleCache?: boolean;
  // opacity is optional here due to union type with MappingTextBoxOptions
  opacity?: number;
  scaleX: number;
  top: number;
  width?: number;
}

interface Coordinate {
  x: number;
  y: number;
}

interface AbsoluteCoordinates {
  tl: Coordinate;
  tr: Coordinate;
  br: Coordinate;
  bl: Coordinate;
}

@Directive()
export class MappingField extends fabric.Rect {
  _counterSign: boolean;
  _id: ID;
  _label: string;
  aCoords: AbsoluteCoordinates;
  ctx: any;
  left: number;
  mapperComponent: MapperComponent;
  top: number;

  // Private Instance Properties
  get type(): string {
    return 'customObject';
  }

  @Output() reRender = new EventEmitter<any>();

  // Instance Properties
  get label(): string {
    return this._label;
  }
  set label(value: string) {
    this._label = value;
    this.reRender.emit();
  }

  get counterSign(): boolean {
    return this._counterSign;
  }

  set counterSign(value: boolean) {
    this._counterSign = value;
    this.setColors();
  }

  get id(): ID {
    return this._id;
  }

  set id(value: ID) {
    this._id = value;
  }

  fixedText: boolean;

  get backgroundColor(): string {
    if (this.fixedText) {
      return appearance.fixedBackgroundColor;
    } else {
      return this.counterSign ? appearance.counterBackgroundColor : appearance.deselectedColor;
    }
  }

  set backgroundColor(color: string) {
    // No-op function required to prevent errors in fabric
  }

  get borderColor(): string {
    if (this.fixedText) {
      return appearance.fixedBorderColor;
    } else {
      return this.counterSign ? appearance.countersignBorderColor : appearance.primaryBorderColor;
    }
  }

  set borderColor(color: string) {
    // No-op function required to prevent errors in fabric
  }

  constructor(text: string, options: MappingFieldOptions, id: ID, mapperComponent: MapperComponent) {
    super();
    this.mapperComponent = mapperComponent;
    this.initialize(options);
    this.setColors();

    this.setControlsVisibility({
      bl: false,
      br: false,
      mb: false,
      ml: false,
      mt: false,
      mtr: false,
      tl: false,
      tr: false
    });
    this.label = text;
    this.id = id;
    this._bindEvents();
  }

  // Listen to Rect events (private)
  _bindEvents(): void {
    this.on('selected', () => {
      this.setColors(0.8);
    });

    this.on('deselected', () => {
      this.setColors();
    });
  }

  setColors(opacity: number = 1): void {
    this.set({
      backgroundColor: this.backgroundColor,
      borderColor: this.borderColor,
      cornerColor: this.borderColor,
      opacity
    });
  }

  // Override the default render method to allow for text (private)
  _render(ctx: any): void {
    this.ctx = ctx;
    const dim = this._getNonTransformedDimensions();
    const localOriginX = dim.x / -2;
    const textOriginX = localOriginX + 10 / this.scaleX;
    const textOriginY = 5;
    let labelWidth;
    let maxTextWidth;
    ctx.font = appearance.fontStyle;
    labelWidth = 'checked' in this ? this.height : ctx.measureText(this.label).width;
    maxTextWidth = labelWidth / this.scaleX;
    ctx.save();
    ctx.translate(textOriginX, textOriginY);
    ctx.fillText(this.label, 0, 0, maxTextWidth);
    ctx.restore();
  }
}
